import React, { useMemo, useState } from "react";
import { Users, Check, ShieldCheck, Mail, ShieldQuestion, Lock, Unlock, Search, Building2, ChevronDown, Loader2, Eye, Pencil, UserPlus, Shield, KeyRound } from "lucide-react";
import { Card } from "@/components/common";
import { ROLE_CHIP, ROLE_LABEL, deDT, downloadCsv, ExportBtn, Sel, Pagination, Drawer } from "./shared";
import { StatCard, Avatar, avatarRing, TwoFACell, accountStatus, DrawerSection, KV } from "./ui";

const SECTION_LABEL = {
  overview: "Überblick", leads: "Anfragen", tasks: "Aufgaben & Freigaben", support: "Support",
  performance: "Besuche & Performance", invoices: "Rechnungen", "next-steps": "Nächste Schritte", report: "Monatsreport",
};

function clientStatusBadge(status, t) {
  const map = {
    aktiv: { dot: "bg-[#A3E635]", txt: "text-[#A3E635]", label: "Aktiv" },
    in_vorbereitung: { dot: "bg-amber-400", txt: "text-amber-400", label: "In Vorbereitung" },
    wartung: { dot: "bg-sky-400", txt: "text-sky-300", label: "Wartung" },
    pausiert: { dot: "bg-amber-400", txt: "text-amber-400", label: "Pausiert" },
    "gekündigt": { dot: "bg-red-500", txt: "text-red-400", label: "Gekündigt" },
    archiviert: { dot: "bg-gray-600", txt: "text-gray-400", label: "Archiviert" },
  };
  const m = map[status] || { dot: "bg-gray-600", txt: "text-gray-400", label: status || "Unbekannt" };
  return { ...m, label: t(m.label) };
}

function ActionPill({ icon: Icon, label, onClick, disabled, tone, testid, title }) {
  const cls = disabled
    ? "border-white/5 text-gray-600 cursor-not-allowed opacity-40"
    : tone === "red"
      ? "border-red-500/25 text-red-300 hover:bg-red-500/10 hover:border-red-500/50"
      : "border-white/10 text-gray-300 hover:bg-white/10 hover:text-white hover:border-white/20";
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testid} title={title || label}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors whitespace-nowrap ${cls}`}>
      <Icon className="w-3.5 h-3.5 shrink-0" /> {label}
    </button>
  );
}

export function ClientUsersTab({ ctx }) {
  const { t, clientUsers, users, clients, busy, actions, me } = ctx;
  const [q, setQ] = useState("");
  const [fCompany, setFCompany] = useState("all");
  const [fRole, setFRole] = useState("all");
  const [f2fa, setF2fa] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [collapsed, setCollapsed] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [detail, setDetail] = useState(null);
  const wrap = (setter) => (v) => { setter(v); setPage(1); };

  const clientById = useMemo(() => Object.fromEntries((clients || []).map((c) => [c.id, c])), [clients]);
  const companies = useMemo(() => Array.from(new Set((clientUsers || []).map((u) => u.company_name).filter(Boolean))).sort(), [clientUsers]);
  const adminByCompany = useMemo(() => {
    const m = {};
    (clientUsers || []).forEach((u) => { if (u.role === "client" && u.company_name && !m[u.company_name]) m[u.company_name] = u; });
    return m;
  }, [clientUsers]);

  const stats = useMemo(() => {
    const c = clientUsers || [];
    const cnt = (k) => c.filter((u) => accountStatus(u, (x) => x).key === k).length;
    return { total: c.length, aktiv: cnt("aktiv"), twofa: c.filter((u) => u.totp_enabled).length, einladung: cnt("einladung"), inaktiv: cnt("deaktiviert"), gesperrt: cnt("gesperrt") };
  }, [clientUsers]);
  const pctOf = (n) => (stats.total ? `${Math.round((n / stats.total) * 100)}%` : "0%");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (clientUsers || []).filter((u) => {
      if (s && !((u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s) || (u.company_name || "").toLowerCase().includes(s))) return false;
      if (fCompany !== "all" && u.company_name !== fCompany) return false;
      if (fRole !== "all" && u.role !== fRole) return false;
      if (f2fa === "on" && !u.totp_enabled) return false;
      if (f2fa === "off" && u.totp_enabled) return false;
      if (fStatus !== "all" && accountStatus(u, (x) => x).key !== fStatus) return false;
      return true;
    });
  }, [clientUsers, q, fCompany, fRole, f2fa, fStatus]);

  const total = filtered.length;
  const pages = Math.max(1, Math.ceil(total / perPage));
  const cur = Math.min(page, pages);
  const pageRows = filtered.slice((cur - 1) * perPage, cur * perPage);
  const groups = useMemo(() => {
    const map = {};
    pageRows.forEach((u) => { const k = u.company_name || t("Ohne Firma"); (map[k] = map[k] || []).push(u); });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [pageRows, t]);

  const resetFilters = () => { setQ(""); setFCompany("all"); setFRole("all"); setF2fa("all"); setFStatus("all"); setPage(1); };
  const exportCsv = () => downloadCsv("kundenbenutzer.csv", [
    { label: "Name", value: "name" }, { label: "E-Mail", value: "email" }, { label: "Firma", value: "company_name" },
    { label: "Rolle", value: (u) => ROLE_LABEL[u.role] || u.role }, { label: "2FA", value: (u) => (u.totp_enabled ? "Aktiv" : "Offen") },
    { label: "Letzter Login", value: (u) => deDT(u.last_login) || "Noch nie" }, { label: "Status", value: (u) => accountStatus(u, (x) => x).label },
  ], filtered);

  return (
    <div className="space-y-4" data-testid="sec-panel-clients">
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3 items-end" data-testid="clientusers-filters">
          <div className="xl:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold block mb-1">{t("Suche")}</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-gray-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => wrap(setQ)(e.target.value)} data-testid="clientusers-search" placeholder={t("Name, E-Mail oder Firma…")} className="w-full bg-black/40 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-xs focus:border-[#A3E635] outline-none" />
            </div>
          </div>
          <FilterField label={t("Firma")}><Sel value={fCompany} onChange={wrap(setFCompany)} testid="clientusers-f-company" options={[{ value: "all", label: t("Alle Firmen") }, ...companies.map((c) => ({ value: c, label: c }))]} /></FilterField>
          <FilterField label={t("Rolle")}><Sel value={fRole} onChange={wrap(setFRole)} testid="clientusers-f-role" options={[{ value: "all", label: t("Alle Rollen") }, { value: "client", label: t("Kunden-Admin") }, { value: "client_staff", label: t("Mitarbeiter") }]} /></FilterField>
          <FilterField label={t("2FA-Status")}><Sel value={f2fa} onChange={wrap(setF2fa)} testid="clientusers-f-2fa" options={[{ value: "all", label: t("Alle") }, { value: "on", label: t("2FA aktiv") }, { value: "off", label: t("2FA offen") }]} /></FilterField>
          <FilterField label={t("Konto-Status")}><Sel value={fStatus} onChange={wrap(setFStatus)} testid="clientusers-f-status" options={[{ value: "all", label: t("Alle") }, { value: "aktiv", label: t("Aktiv") }, { value: "einladung", label: t("Einladung offen") }, { value: "deaktiviert", label: t("Inaktiv") }, { value: "gesperrt", label: t("Gesperrt") }]} /></FilterField>
        </div>
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
          <button onClick={resetFilters} data-testid="clientusers-reset" className="text-xs text-gray-400 hover:text-white px-2 py-1.5 flex items-center gap-1.5">{t("Filter zurücksetzen")}</button>
          <div className="ml-auto"><ExportBtn onClick={exportCsv} label={t("Exportieren")} disabled={filtered.length === 0} title={t("Als CSV exportieren")} testid="clientusers-export" /></div>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="clientusers-stats">
        <StatCard icon={Users} testid="cu-stat-total" label={t("Gesamt")} value={stats.total} sub={t("Benutzer")} accent="gray" />
        <StatCard icon={Check} testid="cu-stat-active" label={t("Aktiv")} value={stats.aktiv} sub={pctOf(stats.aktiv)} accent="lime" />
        <StatCard icon={ShieldCheck} testid="cu-stat-2fa" label={t("Mit 2FA")} value={stats.twofa} sub={pctOf(stats.twofa)} accent={stats.twofa > 0 ? "lime" : "amber"} />
        <StatCard icon={Mail} testid="cu-stat-invite" label={t("Einladung offen")} value={stats.einladung} sub={pctOf(stats.einladung)} accent={stats.einladung > 0 ? "sky" : "gray"} />
        <StatCard icon={ShieldQuestion} testid="cu-stat-inactive" label={t("Inaktiv")} value={stats.inaktiv} sub={pctOf(stats.inaktiv)} accent={stats.inaktiv > 0 ? "amber" : "gray"} />
        <StatCard icon={Lock} testid="cu-stat-locked" label={t("Gesperrt")} value={stats.gesperrt} sub={pctOf(stats.gesperrt)} accent={stats.gesperrt > 0 ? "red" : "gray"} />
      </div>

      {!users && <Card className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#A3E635] mx-auto" /></Card>}
      {users && groups.length === 0 && <Card className="p-6 text-gray-500 text-sm" data-testid="clientusers-empty">{t("Keine Kundenbenutzer gefunden.")}</Card>}

      {groups.map(([company, list]) => {
        const cid = list[0]?.client_id;
        const admin = adminByCompany[company];
        const open = !collapsed[company];
        const cs = clientStatusBadge(clientById[cid]?.status, t);
        return (
          <Card key={company} className="p-0 overflow-hidden" data-testid={`clientgroup-${company}`}>
            <button onClick={() => setCollapsed((c) => ({ ...c, [company]: open }))} data-testid={`clientgroup-toggle-${company}`} className="w-full px-5 py-3.5 border-b border-white/10 flex items-center gap-3 bg-white/[0.02] hover:bg-white/[0.04] transition-colors text-left">
              <div className="w-9 h-9 rounded-xl bg-sky-500/12 flex items-center justify-center shrink-0"><Building2 className="w-[18px] h-[18px] text-sky-300" /></div>
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <span className="font-bold text-sm">{company}</span>
                <span className="text-[11px] text-gray-500">{list.length} {t("Benutzer")}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${open ? "" : "-rotate-90"}`} />
                <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${cs.txt}`}><span className={`w-1.5 h-1.5 rounded-full ${cs.dot}`} />{cs.label}</span>
              </div>
              <div className="ml-auto flex items-center gap-3 shrink-0">
                {admin && <span className="hidden md:inline-flex items-center gap-1.5 text-[11px]"><span className="text-gray-500">{t("Kunden-Admin")}:</span> <span className="text-gray-200 font-semibold">{admin.name}</span></span>}
              </div>
            </button>
            {open && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/5">
                    <th className="text-left font-semibold px-5 py-2.5">{t("Name")}</th>
                    <th className="text-left font-semibold px-3 py-2.5 hidden md:table-cell">{t("E-Mail")}</th>
                    <th className="text-left font-semibold px-3 py-2.5">{t("Rolle")}</th>
                    <th className="text-left font-semibold px-3 py-2.5">2FA</th>
                    <th className="text-left font-semibold px-3 py-2.5 hidden lg:table-cell">{t("Letzter Login")}</th>
                    <th className="text-left font-semibold px-3 py-2.5">{t("Status")}</th>
                    <th className="text-right font-semibold px-5 py-2.5">{t("Aktionen")}</th>
                  </tr></thead>
                  <tbody>
                    {list.map((u) => {
                      const s = accountStatus(u, t);
                      const self = me && u.email === me.email;
                      const locked = u.active === false || u.locked;
                      const working = busy === u.id;
                      return (
                        <tr key={u.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.015]" data-testid={`clientuser-row-${u.id}`}>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-3">
                              <Avatar name={u.name} accent={avatarRing(u)} size="sm" />
                              <div className="min-w-0">
                                <div className="font-semibold truncate max-w-[180px]">{u.name}</div>
                                <div className="text-[11px] text-gray-500 truncate max-w-[180px] md:hidden">{u.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-[11px] text-gray-500 truncate max-w-[220px] hidden md:table-cell">{u.email}</td>
                          <td className="px-3 py-3"><span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${ROLE_CHIP[u.role]}`} data-testid={`clientuser-role-${u.id}`}>{t(ROLE_LABEL[u.role])}</span></td>
                          <td className="px-3 py-3" data-testid={`clientuser-2fa-${u.id}`}><TwoFACell on={u.totp_enabled} t={t} /></td>
                          <td className="px-3 py-3 text-gray-400 text-xs whitespace-nowrap hidden lg:table-cell">{deDT(u.last_login) || t("Noch nie")}</td>
                          <td className="px-3 py-3" data-testid={`clientuser-status-${u.id}`}><span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${s.txt}`}><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}</span></td>
                          <td className="px-5 py-3">
                            <div className="flex items-center justify-end gap-1.5 flex-wrap">
                              {working
                                ? <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                                : <>
                                  <ActionPill icon={Eye} label={t("Anzeigen")} onClick={() => setDetail(u)} testid={`cu-view-${u.id}`} />
                                  <ActionPill icon={Pencil} label={t("Bearbeiten")} onClick={() => actions.edit(u)} testid={`cu-edit-${u.id}`} />
                                  <ActionPill icon={UserPlus} label={t("Zuweisen")} onClick={() => actions.assignCompany(u)} title={t("Firma zuweisen")} testid={`cu-assign-${u.id}`} />
                                  <ActionPill icon={Shield} label={t("Rechte")} onClick={() => actions.rights(u)} title={t("Rechte & Rolle")} testid={`cu-rights-${u.id}`} />
                                  <ActionPill icon={KeyRound} label="2FA" onClick={() => actions.reset2fa(u)} title={t("2FA zurücksetzen")} testid={`cu-2fa-${u.id}`} />
                                  {locked
                                    ? <ActionPill icon={Unlock} label={t("Entsperren")} onClick={() => actions.unlock(u)} testid={`cu-unlock-${u.id}`} />
                                    : <ActionPill icon={Lock} label={t("Sperren")} onClick={() => actions.lock(u)} disabled={self} tone="red" title={self ? t("Eigenes Konto") : t("Konto sperren")} testid={`cu-lock-${u.id}`} />}
                                </>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      })}

      {total > 0 && <Card className="p-0"><Pagination page={cur} setPage={setPage} total={total} perPage={perPage} setPerPage={setPerPage} t={t} testid="clientusers-pagination" /></Card>}

      {detail && <ClientUserDrawer u={detail} t={t} clientStatus={clientStatusBadge(clientById[detail.client_id]?.status, t)} onClose={() => setDetail(null)} actions={actions} />}
    </div>
  );
}

function ClientUserDrawer({ u, t, clientStatus, onClose, actions }) {
  const s = accountStatus(u, t);
  const isAdmin = u.role === "client";
  const sections = u.allowed_sections || [];
  return (
    <Drawer title={t("Benutzerdetails")} onClose={onClose} testid="clientuser-detail-drawer">
      <div className="mb-5 flex items-center gap-3">
        <Avatar name={u.name} accent={avatarRing(u)} size="lg" />
        <div className="min-w-0">
          <div className="font-bold text-base">{u.name}</div>
          <div className="text-xs text-gray-500 truncate">{u.email}</div>
          <span className={`mt-1 inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full border ${ROLE_CHIP[u.role]}`}>{t(ROLE_LABEL[u.role])}</span>
        </div>
      </div>

      <DrawerSection title={t("Übersicht")}>
        <KV label={t("Firma")} value={<span className="inline-flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${clientStatus.dot}`} />{u.company_name || "—"}</span>} />
        <KV label={t("Rolle")} value={t(ROLE_LABEL[u.role])} />
        <KV label={t("Konto-Status")} value={<span className={`inline-flex items-center gap-1.5 ${s.txt}`}><span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}</span>} />
        <KV label="2FA" value={u.totp_enabled ? t("Aktiv") : t("Offen")} accent={u.totp_enabled ? "text-[#A3E635]" : "text-amber-400"} />
        <KV label={t("Letzter Login")} value={deDT(u.last_login) || t("Noch nie")} />
        <KV label={t("Erstellt am")} value={deDT(u.created_at) || "—"} />
      </DrawerSection>

      <DrawerSection title={t("Berechtigungen")}>
        {isAdmin
          ? <KV label={t("Zugriff")} value={t("Vollzugriff (Kunden-Admin)")} accent="text-[#A3E635]" />
          : (
            <div className="py-2">
              {sections.length === 0 ? <span className="text-[11px] text-amber-400">{t("Keine Bereiche freigegeben")}</span>
                : <div className="flex flex-wrap gap-1.5">{sections.map((sec) => <span key={sec} className="text-[11px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-gray-300">{t(SECTION_LABEL[sec] || sec)}</span>)}</div>}
            </div>
          )}
      </DrawerSection>

      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => { onClose(); actions.edit(u); }} data-testid="cu-detail-edit" className="bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"><Pencil className="w-4 h-4 text-[#A3E635]" /> {t("Bearbeiten")}</button>
        <button onClick={() => { onClose(); actions.rights(u); }} data-testid="cu-detail-rights" className="bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"><Shield className="w-4 h-4 text-[#A3E635]" /> {t("Rechte")}</button>
        <button onClick={() => { onClose(); actions.assignCompany(u); }} data-testid="cu-detail-assign" className="bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"><UserPlus className="w-4 h-4 text-[#A3E635]" /> {t("Zuweisen")}</button>
        <button onClick={() => { onClose(); actions.reset2fa(u); }} data-testid="cu-detail-2fa" className="bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"><KeyRound className="w-4 h-4 text-[#A3E635]" /> 2FA</button>
      </div>
    </Drawer>
  );
}

function FilterField({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</label>
      <div className="[&>select]:w-full [&>select]:py-2">{children}</div>
    </div>
  );
}
